import {
    Component,
    Input,
    Output,
    EventEmitter,
    ElementRef,
    AfterViewInit,
    OnDestroy,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    inject,
    signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { PSIC_COMPONENTS } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { globalMessages } from '../global-messages';
import { AnimationComponent } from '../animation/animation.component';

export interface CardField {
    caption: string;
    value?: string;
    valueHtml?: string;
    premium?: string;
    description?: string;
    typeCode?: string;
    descriptionHtml?: string;
    link?: string;
    captionHtml?: string;
    valueIcon?: string;
    actionIcon?: string;
    actionIconDisabled?: boolean;
    isTag?: boolean;
    tagTheme?: string;
    errorTag?: string;
    successTag?: string;
    warningTag?: string;
    hasSeparator?: boolean;
    isActive?: boolean;
    boldValue?: boolean;
    children?: CardField[];
    isUpdating?: boolean;
}

export interface CardSection {
    title: string;
    icon: string;
    fields: CardField[];
    collapsible?: boolean;
    initialVisibleFields?: number;
    loadMoreLabel?: string;
    columns?: string[];
}

export interface CardItem {
    label: string;
    title: string;
    icon: string;
    fields: CardField[];
}

@Component({
    selector: 'app-card-layout',
    standalone: true,
    imports: [CommonModule, MatIconModule, ...PSIC_COMPONENTS, AnimationComponent],
    templateUrl: './card-layout.component.html',
    styleUrl: './card-layout.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardLayoutComponent implements AfterViewInit, OnDestroy {
    @Input() title = '';
    @Input() icon = '';
    @Input() subtitle = '';
    @Input() isLoading = false;
    @Input() failed = false;
    @Input() columns: string[] = [];
    @Input() fields: CardField[] = [];
    @Input() sections: CardSection[] = [];
    @Input() items: CardItem[] = [];
    @Input() noDataMessage = globalMessages.noDataAvailabelMsg;
    @Input() errorMessage = globalMessages.serviceMsgError;
    @Input() isActive = false;
    @Output() retry = new EventEmitter<void>();
    @Output() actionIconClick = new EventEmitter<CardField>();

    isCompact = signal(false);
    expandedSections = signal<Record<string, boolean>>({});
    expandedFields = signal<Record<string, boolean>>({});
    private el = inject(ElementRef);
    private cdr = inject(ChangeDetectorRef);
    private resizeObserver: ResizeObserver;
    globalMessages = globalMessages;

    ngAfterViewInit() {
        const target = this.el.nativeElement.querySelector('.cardLayout');
        if (!target) return;
        this.resizeObserver = new ResizeObserver((entries) => {
            const width = entries[0].contentRect.width;
            if (width === 0) return;
            const compact = width < globalMessages.compactBreakpoint;
            if (compact !== this.isCompact()) {
                this.isCompact.set(compact);
                this.cdr.markForCheck();
            }
        });
        this.resizeObserver.observe(target);
    }

    ngOnDestroy() {
        this.resizeObserver?.disconnect();
    }

    onActionIconClick(field: CardField, event: MouseEvent): void {
        event.stopPropagation();
        this.actionIconClick.emit(field);
    }

    get hasColumns(): boolean {
        return this.columns.length > 0;
    }

    get hasSections(): boolean {
        return this.sections.length > 0;
    }

    get hasItems(): boolean {
        return this.items.length > 0;
    }

    getVisibleFields(section: CardSection): CardField[] {
        if (!section.collapsible) {
            return section.fields;
        }

        const key = this.getSectionKey(section);
        const isExpanded = this.expandedSections()[key];
        const initialVisibleFields = section.initialVisibleFields ?? 3;

        return isExpanded ? section.fields : section.fields.slice(0, initialVisibleFields);
    }

    isSectionExpanded(section: CardSection): boolean {
        return this.expandedSections()[this.getSectionKey(section)] ?? false;
    }

    getSectionKey(section: CardSection): string {
        return `${section.title}-${section.icon}`;
    }

    isFieldExpanded(sectionKey: string, fieldIndex: number): boolean {
        return this.expandedFields()[`${sectionKey}-${fieldIndex}`] ?? false;
    }

    toggleField(sectionKey: string, fieldIndex: number): void {
        const key = `${sectionKey}-${fieldIndex}`;
        const current = this.expandedFields();
        this.expandedFields.set({ ...current, [key]: !current[key] });
        this.cdr.markForCheck();
    }

    toggleSection(section: CardSection): void {
        const key = this.getSectionKey(section);
        const current = this.expandedSections();
        this.expandedSections.set({ ...current, [key]: !current[key] });
        this.cdr.markForCheck();
    }

    shouldShowToggle(section: CardSection): boolean {
        return !!section.collapsible && section.fields.length > (section.initialVisibleFields ?? 3);
    }

    getToggleLabel(section: CardSection): string {
        return this.isSectionExpanded(section) ? 'Show less' : section.loadMoreLabel || 'Show more';
    }

    getToggleIcon(section: CardSection): string {
        return this.isSectionExpanded(section) ? 'chevron-up' : 'chevron-down';
    }

    // Groups items into sections by a stable identity so label-less items (e.g. no logo,
    // no left heading) still form distinct sections instead of merging by shared empty icon.
    // Uses the title base (title without its trailing "(n)" index) so items of the same type
    // group together while different types stay separate.
    private getItemGroupKey(item: CardItem): string {
        const titleBase = (item.title ?? '').replace(/\s*\(\d+\)\s*$/, '').trim();
        return `${item.label ?? ''}::${item.icon ?? ''}::${titleBase}`;
    }

    shouldShowIcon(item: CardItem, index: number): boolean {
        if (index === 0) return true;

        // Check if this is the first item of this section group
        const previousItems = this.items.slice(0, index);
        const key = this.getItemGroupKey(item);
        return !previousItems.some((prevItem) => this.getItemGroupKey(prevItem) === key);
    }

    shouldShowTypeSeparator(item: CardItem, index: number): boolean {
        if (index === 0) return false;

        // Show separator if this is the first item of a new section group
        const previousItem = this.items[index - 1];
        return previousItem && this.getItemGroupKey(previousItem) !== this.getItemGroupKey(item);
    }

    shouldShowSeparatorTopForItemType(item: CardItem, index: number): boolean {
        // Only show separator if this is NOT the first item of this type
        // We can reuse the shouldShowIcon logic - if it returns false, this item should show separator
        return index > 0 && !this.shouldShowIcon(item, index);
    }
}
